import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  FileText, Plus, Calendar, Clock, Mail, Download, Loader2,
  Trash2, Play, CheckCircle2, AlertTriangle, TrendingUp,
  Users, DollarSign, Target, Ticket, BarChart, RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

const REPORT_SECTIONS = [
  { key: 'kpis', label: 'KPIs & Revenue', icon: DollarSign, color: '#10B981' },
  { key: 'campaigns', label: 'Campaign Performance', icon: TrendingUp, color: '#F97316' },
  { key: 'churn', label: 'Churn Risk', icon: AlertTriangle, color: '#EF4444' },
  { key: 'tickets', label: 'Support & SLA', icon: Ticket, color: '#14B8A6' },
  { key: 'nps', label: 'NPS Scores', icon: BarChart, color: '#EC4899' },
  { key: 'goals', label: 'Goal Progress', icon: Target, color: '#8B5CF6' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const ReportPreview = ({ report }) => {
  const { format: fc } = useCurrency();
  if (!report) return null;
  const s = report.sections || {};

  return (
    <div className="space-y-3 animate-fadeIn" data-testid="report-preview">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-700">Report Preview</p>
        <p className="text-[10px] text-gray-400">Generated {new Date(report.generated_at).toLocaleString()}</p>
      </div>

      {s.kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: 'Revenue', value: fc(s.kpis.total_revenue || 0), icon: DollarSign, color: '#10B981' },
            { label: 'Total Leads', value: s.kpis.total_leads || 0, icon: Users, color: '#3B82F6' },
            { label: 'Open Tickets', value: s.kpis.open_tickets || 0, icon: Ticket, color: '#14B8A6' },
            { label: 'Invoices', value: s.kpis.total_invoices || 0, icon: FileText, color: '#8B5CF6' },
          ].map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <item.icon className="h-3.5 w-3.5 mb-1" style={{ color: item.color }} />
              <p className="text-lg font-bold text-text-primary">{item.value}</p>
              <p className="text-[10px] text-gray-400">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      {s.campaigns && (
        <div className="bg-orange-50 rounded-lg p-2.5 border border-orange-100">
          <p className="text-xs font-medium text-orange-800">Campaigns</p>
          <p className="text-[11px] text-orange-700">{s.campaigns.active_email_campaigns} active email, {s.campaigns.active_push_campaigns} push</p>
        </div>
      )}

      {s.churn && (
        <div className={cn("rounded-lg p-2.5 border", s.churn.high_risk_customers > 0 ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100")}>
          <p className="text-xs font-medium text-gray-800">Churn Risk</p>
          <p className="text-[11px]">{s.churn.high_risk_customers} high risk, {s.churn.medium_risk_customers} medium risk</p>
        </div>
      )}

      {s.tickets && (
        <div className="bg-teal-50 rounded-lg p-2.5 border border-teal-100">
          <p className="text-xs font-medium text-teal-800">Support</p>
          <p className="text-[11px] text-teal-700">{s.tickets.open_tickets} open, {s.tickets.resolved_tickets} resolved — SLA: {s.tickets.sla_compliance}%</p>
        </div>
      )}

      {s.nps && (
        <div className="bg-pink-50 rounded-lg p-2.5 border border-pink-100">
          <p className="text-xs font-medium text-pink-800">NPS Score: {s.nps.nps_score}</p>
          <p className="text-[11px] text-pink-700">{s.nps.total_responses} responses</p>
        </div>
      )}

      {s.goals && (
        <div className="bg-purple-50 rounded-lg p-2.5 border border-purple-100">
          <p className="text-xs font-medium text-purple-800">Goals: {s.goals.completed_goals}/{s.goals.total_goals} completed ({s.goals.completion_rate}%)</p>
        </div>
      )}
    </div>
  );
};

export default function ScheduledReportsPage() {
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [selectedSections, setSelectedSections] = useState(['kpis', 'campaigns', 'churn', 'tickets', 'nps', 'goals']);
  const [newSchedule, setNewSchedule] = useState({
    name: 'Weekly Business Report', frequency: 'weekly', day_of_week: 'monday', time: '09:00', recipients: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [sRes, hRes] = await Promise.all([
        axios.get(`${API}/api/scheduled-reports/schedules`),
        axios.get(`${API}/api/scheduled-reports/history`),
      ]);
      setSchedules(sRes.data.schedules || []);
      setHistory(hRes.data.reports || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/scheduled-reports/generate`, {
        sections: selectedSections, period: 'last_30_days',
      });
      setGeneratedReport(data);
      toast.success('Report generated successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      await axios.post(`${API}/api/scheduled-reports/schedules`, {
        ...newSchedule,
        recipients: newSchedule.recipients.split(',').map(e => e.trim()).filter(Boolean),
        sections: selectedSections,
      });
      toast.success('Schedule created');
      setShowCreate(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await axios.delete(`${API}/api/scheduled-reports/schedules/${id}`);
      toast.success('Schedule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  const downloadReport = () => {
    if (!generatedReport) return;
    const blob = new Blob([JSON.stringify(generatedReport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `konnect-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSection = (key) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="reports-loading">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-40 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="scheduled-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary" data-testid="reports-title">
              Reports<PageInfoTooltip pageKey="scheduled-reports" />
            </h1>
            <p className="text-[11px] text-gray-medium">Generate reports instantly or schedule automatic delivery</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowCreate(!showCreate)} data-testid="new-schedule-btn">
            <Calendar className="h-3.5 w-3.5 mr-1" /> New Schedule
          </Button>
          <Button
            size="sm"
            className="bg-emerald-brand hover:bg-emerald-600"
            onClick={handleGenerate}
            disabled={generating || selectedSections.length === 0}
            data-testid="generate-report-btn"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
            Generate Now
          </Button>
        </div>
      </div>

      {/* Section Selector */}
      <Card className="bg-white border-gray-border" data-testid="section-selector">
        <CardContent className="p-4">
          <p className="text-xs font-medium text-gray-700 mb-2.5">Report Sections</p>
          <div className="flex flex-wrap gap-2">
            {REPORT_SECTIONS.map((sec) => {
              const active = selectedSections.includes(sec.key);
              return (
                <button
                  key={sec.key}
                  onClick={() => toggleSection(sec.key)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors",
                    active ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                  )}
                  data-testid={`section-toggle-${sec.key}`}
                >
                  <sec.icon className="h-3 w-3" style={{ color: active ? sec.color : '#9CA3AF' }} />
                  {sec.label}
                  {active && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Create Schedule Form */}
      {showCreate && (
        <Card className="bg-white border-gray-border border-blue-200 animate-fadeIn" data-testid="create-schedule-form">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">New Scheduled Report</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-gray-600 block mb-1">Report Name</label>
                <Input
                  value={newSchedule.name}
                  onChange={(e) => setNewSchedule(p => ({ ...p, name: e.target.value }))}
                  className="text-xs"
                  data-testid="schedule-name-input"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-600 block mb-1">Frequency</label>
                <select
                  value={newSchedule.frequency}
                  onChange={(e) => setNewSchedule(p => ({ ...p, frequency: e.target.value }))}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg"
                  data-testid="schedule-frequency-select"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {newSchedule.frequency === 'weekly' && (
                <div>
                  <label className="text-[11px] font-medium text-gray-600 block mb-1">Day of Week</label>
                  <select
                    value={newSchedule.day_of_week}
                    onChange={(e) => setNewSchedule(p => ({ ...p, day_of_week: e.target.value }))}
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg capitalize"
                    data-testid="schedule-day-select"
                  >
                    {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-[11px] font-medium text-gray-600 block mb-1">Time</label>
                <Input
                  type="time"
                  value={newSchedule.time}
                  onChange={(e) => setNewSchedule(p => ({ ...p, time: e.target.value }))}
                  className="text-xs"
                  data-testid="schedule-time-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] font-medium text-gray-600 block mb-1">Recipients (comma separated emails)</label>
                <Input
                  value={newSchedule.recipients}
                  onChange={(e) => setNewSchedule(p => ({ ...p, recipients: e.target.value }))}
                  placeholder="admin@company.com, manager@company.com"
                  className="text-xs"
                  data-testid="schedule-recipients-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" className="bg-emerald-brand hover:bg-emerald-600 text-xs" onClick={handleCreateSchedule} data-testid="save-schedule-btn">
                <Calendar className="h-3.5 w-3.5 mr-1" /> Create Schedule
              </Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Generated Report Preview */}
        <Card className="bg-white border-gray-border" data-testid="report-result">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Latest Report</CardTitle>
              {generatedReport && (
                <Button variant="outline" size="sm" className="text-xs" onClick={downloadReport} data-testid="download-report-btn">
                  <Download className="h-3.5 w-3.5 mr-1" /> Download
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {generatedReport ? (
              <ReportPreview report={generatedReport} />
            ) : (
              <div className="py-8 text-center">
                <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400">Click "Generate Now" to create a report</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduled Reports + History */}
        <div className="space-y-4">
          {/* Active Schedules */}
          <Card className="bg-white border-gray-border" data-testid="active-schedules">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-500" /> Active Schedules
                <Badge variant="outline" className="text-[10px]">{schedules.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {schedules.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">No scheduled reports yet</p>
              ) : (
                <div className="space-y-2">
                  {schedules.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                      <div>
                        <p className="text-xs font-medium text-text-primary">{s.name}</p>
                        <p className="text-[10px] text-gray-400 capitalize">
                          {s.frequency} {s.day_of_week ? `on ${s.day_of_week}` : ''} at {s.time}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Mail className="h-2.5 w-2.5 text-gray-400" />
                          <p className="text-[9px] text-gray-400">{(s.recipients || []).join(', ')}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-600 h-7 w-7 p-0"
                        onClick={() => handleDeleteSchedule(s.id)}
                        data-testid={`delete-schedule-${i}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Report History */}
          <Card className="bg-white border-gray-border" data-testid="report-history">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" /> Report History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {history.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">No reports generated yet</p>
              ) : (
                <div className="space-y-1.5">
                  {history.slice(0, 10).map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2.5 rounded bg-gray-50">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                        <span className="text-[11px] text-gray-700">
                          {new Date(r.generated_at).toLocaleDateString()} {new Date(r.generated_at).toLocaleTimeString()}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-[9px]">
                        {Object.keys(r.sections || {}).length} sections
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
